import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector, useDispatch } from 'react-redux';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import AssetActions from 'components/AssetActions';
import AssetsCollectionEntities from 'components/AssetsCollectionEntities';
import AssetsCollectionScreens from 'components/AssetsCollectionScreens';
import DocumentThumbnail from 'components/DocumentThumbnail';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import Loader from 'components/Loader';
import { ModalType } from 'const';
import { getAssetsCollectionData, getStoryCardsPreviews } from 'containers/Documents/actions';
import { showModal } from 'containers/ModalManager/actions';
import * as Models from 'models';
import { isMagicForm, isStoryCard } from 'utils/entityType';
import { assetsCollectionSelector } from './selectors';
import styles from './styles.module.scss';
import { useAssetsCollectionDragSource } from './useAssetsCollectionDragSource';

type Props = {
  document: Models.AssetsCollectionMap;
  dataTestId?: string;
};

const AssetsCollection: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { document, dataTestId = 'assetsCollection' } = props;
  const { lastRefreshTime, isNotificationAccessError } = useSelector(assetsCollectionSelector);
  const { isDragging, draggable, connectDragSource, connectDragPreview } = useAssetsCollectionDragSource(document);

  const documentId = (document as Models.DocumentMap).get('id');

  const handleToggleExpand = (): void => {
    setIsExpanded(prev => !prev);
  };
  useEffect(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevLastRefreshTimeRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      dataLoaded &&
    lastRefreshTime &&
    prevLastRefreshTimeRef.current !== lastRefreshTime
    ) {
      setDataLoaded(false);
    }
    prevLastRefreshTimeRef.current = lastRefreshTime;

  }, [lastRefreshTime, dataLoaded]);

  useEffect(() => {
    const nextRelatedDocuments: Models.CombinedDocumentsMap = document.get('documents');
    if (!dataLoaded && nextRelatedDocuments?.size > 0) {
      setDataLoaded(true);
    }
  }, [document, dataLoaded]);

  useEffect(() => {
    if (isExpanded && isNotificationAccessError) {
      handleToggleExpand();

      return;
    }
    if (!dataLoaded && isExpanded) {
      dispatch(getAssetsCollectionData(documentId));
    }
  }, [isExpanded, isNotificationAccessError, dataLoaded, dispatch, documentId]);

  const retrievePreview = useCallback(() => {
    if (isStoryCard(document)) {
      dispatch(getStoryCardsPreviews([document.toJS() as Models.StoryCard]));
    }
  }, [document, dispatch]);

  const showDetailsModal = useCallback((previewUrls: string[]) => {
    const {
      description = '',
      name,
      number,
      product,
      status,
    } = document.toJS() as Models.StoryCard;

    dispatch(showModal(ModalType.STORY_CARD_DETAILS, { description, name, number, previewUrls, product, status }));
  }, [document, dispatch]);

  const showStoryCardDetails = useCallback(() => {
    const { _previewUrls: previewUrls } = document.toJS() as Models.StoryCard;
    showDetailsModal(previewUrls ?? []);
  }, [document, showDetailsModal]);

  const showScreenDetails = useCallback((previewUrl: string) => {
    showDetailsModal([previewUrl]);
  }, [showDetailsModal]);

  const areActionsAvailable = (): boolean => {
    const previewUrls = (document as Models.StoryCardMap).get('_previewUrls');
    const isPreviewBeingLoaded = (document as Models.StoryCardMap).get('_isPreviewBeingLoaded');
    if (!isStoryCard(document)) {
      return false;
    }
    if (isPreviewBeingLoaded) {
      return false;
    }
    if (!previewUrls) {
      return false;
    }

    return previewUrls.size !== 0;
  };

  const showDocumentsByScreens = (): boolean => {
    const entities = (document as Models.StoryCardMap).get('entities');
    const screenIds = entities ? entities.get('screenIds') : null;

    return !!screenIds && screenIds.size > 1;
  };

  const renderContent = (): JSX.Element => {
    return showDocumentsByScreens()
      ? <AssetsCollectionScreens
        document={document}
        showScreenDetails={showScreenDetails}
        dataTestId={`${dataTestId}-screens`} />
      : <AssetsCollectionEntities
        document={document}
        screenOrder={1}
        dataTestId={`${dataTestId}-entities`} />;
  };

  const name = (document as Models.DocumentMap).get('name');
  const number = (document as Models.DocumentMap).get('number');
  const brand = ((document as Models.DocumentMap).getIn(['product']) || []).join(', ');
  const description = (document as Models.DocumentMap).get('description');
  const previewUrls = (document as Models.StoryCardMap).get('_previewUrls');
  const isPreviewBeingLoaded = (document as Models.StoryCardMap).get('_isPreviewBeingLoaded');

  return (
    <div className={classNames(styles.AssetsCollection, { [styles.isDragging]: isDragging })}
      data-testid={dataTestId}
    >
      <div className={styles.contentInvisible}>
        {connectDragSource && connectDragSource(
          <div className={classNames(styles.card__wrapper, { [styles.draggable]: draggable })}
            data-testid={`${dataTestId}-cardWrapper`}
          >
            <div className={styles.box} data-testid={`${dataTestId}-box`}>
              <DocumentThumbnail
                loaded={!!previewUrls}
                loading={isPreviewBeingLoaded}
                getThumb={retrievePreview}
                url={previewUrls && previewUrls.get(0)}
                placeholder={(isStoryCard(document) || isMagicForm(document))
                  ? <Icon type={IconType.IMAGE} size="lg" color="disabled" />
                  : <Icon type={IconType.TEXT_COLLECTION} size="xl" color="disabled" />
                }
                dataTestId={`${dataTestId}-box-documentThumbnail`}
              />
              {
                areActionsAvailable() &&
                <AssetActions onPreviewOpen={showStoryCardDetails} dataTestId={`${dataTestId}-box-assetActions`} />
              }
            </div>
            <AccessibleDiv
              className={styles.info}
              onClick={handleToggleExpand}
              data-testid={`${dataTestId}-info`}
            >
              <div className={styles.info__primary} data-testid={`${dataTestId}-info-name`}>{name}</div>
              <div className={styles.info__secondary} data-testid={`${dataTestId}-info-description`}>{description}</div>
              <div className={styles.info__bottom__container} data-testid={`${dataTestId}-info-bottom`}>
                <div className={styles.info__bottom}>
                  <div className={styles.info__bottom__number} data-testid={`${dataTestId}-info-bottom-number`}>{number}</div>
                  <div className={styles.info__bottom__brand} data-testid={`${dataTestId}-info-bottom-brand`}>{brand}</div>
                </div>
                <div className={styles.chevron} data-testid={`${dataTestId}-info-bottom-chevron`}>
                  <Icon
                    type={isExpanded ? IconType.CHEVRON_UP : IconType.CHEVRON_DOWN}
                    size="badge"
                    color="primary"
                  />
                </div>
              </div>
            </AccessibleDiv>
          </div>,
        )}
        <AnimateHeight
          duration={300}
          easing="ease-out"
          height={isExpanded ? 'auto' : 0}
        >
          {
            !dataLoaded
              ? <Loader />
              : renderContent()
          }
        </AnimateHeight>
      </div>
    </div>
  );
};

export default AssetsCollection;
